import type { AppTheme, Preferences } from "@closer/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect, useMemo } from "react";

import { api } from "./apiClient";
import { useAuth } from "./authContext";
import { applyTheme, normalizeTheme, resolveTheme, watchSystemTheme } from "./theme";

/**
 * Hesap seviyesi tercihler (CLOS-105 `[066]`).
 *
 * ### Anahtar TENANT'LI DEĞİL
 *
 * `tenantKey` KULLANILMIYOR ve bu kartın kriteri: *"tercih ... çalışma alanlarından
 * bağımsız korunur"*, *"çalışma alanı değişiminde kaybolmaz veya tenant verisine
 * dönüşmez."* Tenant'lı anahtarlansaydı `clearTenantQueries` her alan değişiminde
 * tercihi siler ve kullanıcı bir anlığına varsayılan temaya düşerdi (`[043]` konvansiyonu
 * hesap düzeyi sorguları bilerek dışarıda bırakıyor).
 */
const VARSAYILAN: Preferences = { theme: "system", locale: "tr", supportedLocales: ["tr"] };

interface PreferencesValue {
  preferences: Preferences;
  /** Uygulanan tema — `system` çözümlendikten sonraki değer. */
  setTheme: (theme: AppTheme) => Promise<void>;
  setLocale: (locale: string) => Promise<void>;
  isSaving: boolean;
}

const PreferencesContext = createContext<PreferencesValue | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const sorgu = useQuery({
    queryKey: ["preferences"],
    enabled: user !== null,
    queryFn: async () => (await api<Preferences>("/api/profile/preferences")).data,
  });

  /**
   * Oturum yokken de bir tercih nesnesi var: giriş ekranları da tema uygulayabilmeli ve
   * `undefined` kontrolü her tüketiciye yayılmamalı. Bozuk değer `normalizeTheme` ile
   * güvenli varsayılana düşüyor (kart kriteri).
   */
  const preferences: Preferences = useMemo(() => {
    if (!sorgu.data) return VARSAYILAN;
    return { ...sorgu.data, theme: normalizeTheme(sorgu.data.theme) };
  }, [sorgu.data]);

  /** Tercih değiştiğinde DOM'a yaz. */
  useEffect(() => {
    applyTheme(resolveTheme(preferences.theme));
  }, [preferences.theme]);

  /**
   * ⚠ `system` seçiliyken işletim sistemi DEĞİŞİKLİĞİ izleniyor (kartın runtime koşulu).
   * Bir kez okuyup bırakmak yeterli değildi: kullanıcı gün batımında sistemini koyuya
   * alınca uygulama açık kalırdı.
   */
  useEffect(() => {
    if (preferences.theme !== "system") return;
    return watchSystemTheme((resolved) => applyTheme(resolved));
  }, [preferences.theme]);

  const kaydet = useMutation({
    mutationFn: async (girdi: { theme?: AppTheme; locale?: string }) =>
      (
        await api<Preferences>("/api/profile/preferences", {
          method: "PATCH",
          body: JSON.stringify(girdi),
        })
      ).data,
    onSuccess: (data) => queryClient.setQueryData(["preferences"], data),
  });

  const value = useMemo<PreferencesValue>(
    () => ({
      preferences,
      setTheme: async (theme) => {
        // Önce EKRANA uygula: kullanıcı sonucu ağ turunu beklemeden görsün. Sunucu
        // reddederse `onSuccess` gelmez ve bir sonraki okuma gerçeği geri getirir.
        applyTheme(resolveTheme(theme));
        await kaydet.mutateAsync({ theme });
      },
      setLocale: async (locale) => {
        await kaydet.mutateAsync({ locale });
      },
      isSaving: kaydet.isPending,
    }),
    [preferences, kaydet],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error("usePreferences, PreferencesProvider içinde kullanılmalıdır");
  return ctx;
}
